import React, { useState, useEffect } from 'react'
import FyMonthlyBarChart from '../../components/Charts/FyMonthlyBarChart'
import { currentFyLabel } from '../../utils/financialYearMonths'
import { 
  Users,
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Brain,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  MessageSquare,
  FileText,
  Briefcase,
  Phone,
  Mail,
  UserPlus,
  Heart,
  ChevronRight,
  PieChart,
  LineChart,
  TrendingDown as TrendingDownIcon,
  Award,
  UserCheck,
  RefreshCw,
  Building2,
  Headphones,
  Repeat,
  Upload
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import crmDashboardService from '../../services/crmDashboardService'
import { useModuleView } from '../../hooks/useModuleView'
import ModuleOverviewCard from '../../components/Module/ModuleOverviewCard'
import ModuleViewBackLink from '../../components/Module/ModuleViewBackLink'
import { CELYNTIQ_CRM_LOGIN_URL } from '../../config/footerNavConfig'
// Consolidated CSS is imported globally

/** When true, show in-app CRM hub. CRM product is Celyntiq — keep false to avoid duplicate UI. */
const SHOW_IN_APP_CRM_HUB = false

const CRM = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)

  // Redirect to Celyntiq CRM (external) — keep in-app routes for later if needed
  useEffect(() => {
    if (SHOW_IN_APP_CRM_HUB) return
    window.open(CELYNTIQ_CRM_LOGIN_URL, '_blank', 'noopener,noreferrer')
    navigate('/sales', { replace: true })
  }, [navigate])

  // Load dashboard data
  useEffect(() => {
    if (!SHOW_IN_APP_CRM_HUB) return
    if (moduleView === 'overview') {
      loadDashboardData()
    }
  }, [moduleView])

  if (!SHOW_IN_APP_CRM_HUB) {
    return (
      <div className="sales-dashboard" style={{ padding: 40, textAlign: 'center' }}>
        <p style={{ color: 'var(--color-text-secondary, #5B6478)', margin: 0 }}>
          Opening CRM in a new tab…
        </p>
      </div>
    )
  }

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const response = await crmDashboardService.getDashboardStats()
      if (response.success) {
        setDashboardData(response.data)
      } else {
        if (!response._errorHandled) {
          toast.error(response.message || 'Failed to load dashboard data')
        }
      }
    } catch (error) {
      console.error('Error loading CRM dashboard data:', error)
      if (!error._errorHandled) {
        if (error.response) {
          toast.error(error.response.data?.message || 'Failed to load dashboard data')
        } else if (!error.request) {
          toast.error('Failed to load dashboard data')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now'
    const date = new Date(timestamp)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1 style={{textAlign: 'left', margin: '0 0 4px 0'}}>CRM</h1>
          <p style={{textAlign: 'left', margin: 0}}>Manage customer relationships and track business performance</p>
        </div>
      </div>

      {/* Tab Content */}
      {moduleView === 'overview' && (
        <div className="tab-content">
          <ModuleViewBackLink onClick={() => navigate('/crm')} />

          {/* Key Metrics Grid */}
          <div className="dashboard-metrics-grid">
            {/* Customer Metrics */}
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <Users size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.totalCustomers?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.totalCustomers?.change >= 0 ? '+' : ''}{dashboardData?.totalCustomers?.change?.toFixed(1) || 0}%</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.totalCustomers?.count?.toLocaleString() || '0')}
              </div>
              <div className="metric-title">Total Customers</div>
              <div className="metric-subtitle">vs last month</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <UserPlus size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.newLeads?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.newLeads?.change >= 0 ? '+' : ''}{dashboardData?.newLeads?.change || 0}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.newLeads?.count || 0)}
              </div>
              <div className="metric-title">New Leads</div>
              <div className="metric-subtitle">This month</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <Heart size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.customerSatisfaction?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.customerSatisfaction?.change >= 0 ? '+' : ''}{dashboardData?.customerSatisfaction?.change?.toFixed(1) || 0}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading
                  ? '...'
                  : dashboardData?.customerSatisfaction?.unavailable || dashboardData?.customerSatisfaction?.rating == null
                    ? '—'
                    : `${dashboardData.customerSatisfaction.rating}/5`}
              </div>
              <div className="metric-title">Customer Satisfaction</div>
              <div className="metric-subtitle">Industry avg: 4.1/5</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <MessageSquare size={16} />
                </div>
                <div className="metric-trend neutral">
                  <Clock size={12} />
                  <span>{dashboardData?.responseTime?.status || 'STABLE'}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : `${dashboardData?.responseTime?.hours || 0}h`}
              </div>
              <div className="metric-title">Response Time</div>
              <div className="metric-subtitle">Average response</div>
            </div>
          </div>

          {/* Action Items & Alerts */}
          <div className="dashboard-action-section">
            <div className="dashboard-action-items">
              <div className="section-header">
                <h3>CRM Action Items</h3>
                <span className="item-count">{dashboardData?.actionItems?.length || 0} items</span>
              </div>
              <div className="action-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading action items...</div>
                ) : dashboardData?.actionItems && dashboardData.actionItems.length > 0 ? (
                  dashboardData.actionItems.map((item, index) => {
                    const iconMap = {
                      lead: AlertTriangle,
                      checkin: Phone,
                      profile: Mail
                    }
                    const Icon = iconMap[item.type] || AlertTriangle
                    return (
                      <div key={index} className={`action-item ${item.priority}`}>
                        <div className="action-icon">
                          <Icon size={16} />
                        </div>
                        <div className="action-content">
                          <div className="action-title">{item.title}</div>
                          <div className="action-description">{item.description}</div>
                        </div>
                        <div className="action-time">{formatTimeAgo(item.timestamp)}</div>
                      </div>
                    )
                  })
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No action items</div>
                )}
              </div>
            </div>

            <div className="dashboard-team-activity">
              <div className="section-header">
                <h3>CRM Team Activity</h3>
                <span className="activity-count">{dashboardData?.teamActivity?.length || 0} updates</span>
              </div>
              <div className="activity-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading team activity...</div>
                ) : dashboardData?.teamActivity && dashboardData.teamActivity.length > 0 ? (
                  dashboardData.teamActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-avatar">{activity.employeeInitials || 'UN'}</div>
                      <div className="activity-content">
                        <div className="activity-text">
                          <strong>{activity.employeeName || 'Unknown'}</strong> {activity.activity}
                        </div>
                        <div className="activity-time">{formatTimeAgo(activity.timestamp)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No recent activity</div>
                )}
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="dashboard-charts-section">
            <div className="dashboard-chart-card revenue-chart">
              <div className="chart-header">
                <h3>Customer Growth</h3>
                <p>{`FY ${currentFyLabel()}`}</p>
              </div>
              <div className="chart-content">
                <FyMonthlyBarChart emptyMessage="No data for this financial year" />
              </div>
            </div>

            <div className="dashboard-chart-card customer-health">
              <div className="chart-header">
                <h3>Customer Health Distribution</h3>
                <p>Current customer base</p>
              </div>
              <div className="chart-content">
                <div className="health-distribution">
                  <div className="health-segment excellent">
                    <div className="segment-bar" style={{width: '40%'}}></div>
                    <div className="segment-info">
                      <span className="segment-label">Excellent</span>
                      <span className="segment-count">498</span>
                    </div>
                  </div>
                  <div className="health-segment good">
                    <div className="segment-bar" style={{width: '35%'}}></div>
                    <div className="segment-info">
                      <span className="segment-label">Good</span>
                      <span className="segment-count">436</span>
                    </div>
                  </div>
                  <div className="health-segment at-risk">
                    <div className="segment-bar" style={{width: '20%'}}></div>
                    <div className="segment-info">
                      <span className="segment-label">At Risk</span>
                      <span className="segment-count">249</span>
                    </div>
                  </div>
                  <div className="health-segment churned">
                    <div className="segment-bar" style={{width: '5%'}}></div>
                    <div className="segment-info">
                      <span className="segment-label">Churned</span>
                      <span className="segment-count">64</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'masters' && (
        <div className="tab-content">
          <div className="crm-masters-content">
            <div className="crm-masters-grid">
              <div 
                className="crm-master-card clickable"
                onClick={() => navigate('/crm/lead-source-master')}
              >
                <div className="crm-master-icon">
                  <Target size={24} />
                </div>
                <div className="crm-master-content">
                  <h3>Lead Sources</h3>
                  <p>Track and manage lead generation sources</p>
                  <div className="crm-master-count">—</div>
                </div>
                <div className="crm-master-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>
              <div 
                className="crm-master-card clickable"
                onClick={() => navigate('/crm/prospect-master')}
              >
                <div className="crm-master-icon">
                  <Users size={24} />
                </div>
                <div className="crm-master-content">
                  <h3>Prospects</h3>
                  <p>Manage prospect details</p>
                  <div className="crm-master-count">—</div>
                </div>
                <div className="crm-master-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>
              <div 
                className="crm-master-card clickable"
                onClick={() => navigate('/crm/campaign-master')}
              >
                <div className="crm-master-icon">
                  <Activity size={24} />
                </div>
                <div className="crm-master-content">
                  <h3>Campaigns</h3>
                  <p>Marketing campaigns and outreach programs</p>
                  <div className="crm-master-count">—</div>
                </div>
                <div className="crm-master-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>
              <div 
                className="crm-master-card clickable"
                onClick={() => navigate('/crm/upload-prospect')}
              >
                <div className="crm-master-icon">
                  <Upload size={24} />
                </div>
                <div className="crm-master-content">
                  <h3>Upload Prospect</h3>
                  <p>Bulk upload prospects from Excel</p>
                  <div className="crm-master-count">Upload</div>
                </div>
                <div className="crm-master-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'transactions' && (
        <div className="tab-content">
          <div className="crm-activities-content">
            <div className="crm-activities-header">
              <h2>CRM Transactions</h2>
              <p>Track and manage all customer relationship transactions</p>
            </div>
            <div className="crm-activities-grid">
              <ModuleOverviewCard
                prefix="crm-activity"
                moduleName="CRM"
                onClick={() => navigate('/crm?view=overview')}
              />
              <div 
                className="crm-activity-card clickable"
                onClick={() => navigate('/crm/lead-management')}
              >
                <div className="crm-activity-icon">
                  <UserPlus size={24} />
                </div>
                <div className="crm-activity-content">
                  <h3>Lead Management</h3>
                  <p>Capture, qualify and track leads from prospects</p>
                  <div className="crm-activity-count">47 Active Leads</div>
                </div>
                <div className="crm-activity-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>
              <div 
                className="crm-activity-card clickable"
                onClick={() => navigate('/crm/lead-tracking')}
              >
                <div className="crm-activity-icon">
                  <Target size={24} />
                </div>
                <div className="crm-activity-content">
                  <h3>Lead Tracking</h3>
                  <p>Monitor lead status and pipeline progression</p>
                  <div className="crm-activity-count">156 Active</div>
                </div>
                <div className="crm-activity-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>
              <div 
                className="crm-activity-card clickable"
                onClick={() => navigate('/crm/demo-scheduling')}
              >
                <div className="crm-activity-icon">
                  <Activity size={24} />
                </div>
                <div className="crm-activity-content">
                  <h3>Demo Schedule</h3>
                  <p>Schedule and conduct product demonstrations</p>
                  <div className="crm-activity-count">23 Scheduled</div>
                </div>
                <div className="crm-activity-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>
              <div 
                className="crm-activity-card clickable"
                onClick={() => navigate('/crm/commercial-proposal')}
              >
                <div className="crm-activity-icon">
                  <FileText size={24} />
                </div>
                <div className="crm-activity-content">
                  <h3>Commercial Proposal</h3>
                  <p>Create and track commercial proposals and quotes</p>
                  <div className="crm-activity-count">18 Pending</div>
                </div>
                <div className="crm-activity-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>
              <div 
                className="crm-activity-card clickable"
                onClick={() => navigate('/crm/emails')}
              >
                <div className="crm-activity-icon">
                  <Mail size={24} />
                </div>
                <div className="crm-activity-content">
                  <h3>Emails</h3>
                  <p>Compose, send, and track email communications</p>
                  <div className="crm-activity-count">156 Sent</div>
                </div>
                <div className="crm-activity-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>
              <div 
                className="crm-activity-card clickable"
                onClick={() => navigate('/crm/calls')}
              >
                <div className="crm-activity-icon">
                  <Phone size={24} />
                </div>
                <div className="crm-activity-content">
                  <h3>Calls</h3>
                  <p>Track and log customer calls and conversations</p>
                  <div className="crm-activity-count">89 Logged</div>
                </div>
                <div className="crm-activity-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>
              <div className="crm-activity-card">
                <div className="crm-activity-icon">
                  <Calendar size={24} />
                </div>
                <div className="crm-activity-content">
                  <h3>Meetings</h3>
                  <p>Schedule and track customer meetings</p>
                  <div className="crm-activity-count">34 Scheduled</div>
                </div>
              </div>
              <div 
                className="crm-activity-card clickable"
                onClick={() => navigate('/crm/follow-up')}
              >
                <div className="crm-activity-icon">
                  <CheckCircle size={24} />
                </div>
                <div className="crm-activity-content">
                  <h3>Follow-ups</h3>
                  <p>Manage tasks and follow-up activities</p>
                  <div className="crm-activity-count">67 Pending</div>
                </div>
                <div className="crm-activity-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'reports' && (
        <div className="tab-content">
          <div className="crm-reports-content">
            <div className="crm-reports-header">
              <h2>CRM Reports</h2>
              <p>Comprehensive customer relationship analytics and reporting</p>
            </div>
            <div className="crm-reports-grid">
              {/* Sales Performance Reports */}
              <div 
                className="crm-report-card clickable"
                onClick={() => window.open('/crm/reports/sales-performance', '_blank')}
              >
                <div className="crm-report-icon">
                  <PieChart size={24} />
                </div>
                <div className="crm-report-content">
                  <h3>Sales Performance</h3>
                  <p>Sales pipeline, forecasts, and team performance</p>
                  <div className="crm-report-status">Ready to Develop</div>
                </div>
                <div className="crm-report-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>

              {/* Lead Reports */}
              <div 
                className="crm-report-card clickable"
                onClick={() => window.open('/crm/reports/lead-reports', '_blank')}
              >
                <div className="crm-report-icon">
                  <Users size={24} />
                </div>
                <div className="crm-report-content">
                  <h3>Lead Reports</h3>
                  <p>Lead generation and conversion tracking</p>
                  <div className="crm-report-status">Ready to Develop</div>
                </div>
                <div className="crm-report-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>

              {/* Customer Analytics */}
              <div 
                className="crm-report-card clickable"
                onClick={() => window.open('/crm/reports/customer-analytics', '_blank')}
              >
                <div className="crm-report-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="crm-report-content">
                  <h3>Customer Analytics</h3>
                  <p>Customer behavior and engagement analysis</p>
                  <div className="crm-report-status">Ready to Develop</div>
                </div>
                <div className="crm-report-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>

              {/* Activity Reports */}
              <div className="crm-report-card clickable">
                <div className="crm-report-icon">
                  <Activity size={24} />
                </div>
                <div className="crm-report-content">
                  <h3>Activity Reports</h3>
                  <p>Call, follow-up, and team productivity metrics</p>
                  <div className="crm-report-status">Ready to Develop</div>
                </div>
                <div className="crm-report-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>

              {/* Engagement Reports */}
              <div className="crm-report-card clickable">
                <div className="crm-report-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="crm-report-content">
                  <h3>Engagement Reports</h3>
                  <p>Customer engagement and satisfaction metrics</p>
                  <div className="crm-report-status">Updated Today</div>
                </div>
                <div className="crm-report-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>

              {/* Campaign Performance */}
              <div className="crm-report-card clickable">
                <div className="crm-report-icon">
                  <Target size={24} />
                </div>
                <div className="crm-report-content">
                  <h3>Campaign Performance</h3>
                  <p>Marketing campaign effectiveness and ROI</p>
                  <div className="crm-report-status">Updated Today</div>
                </div>
                <div className="crm-report-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>

              {/* Financial Reports */}
              <div className="crm-report-card clickable">
                <div className="crm-report-icon">
                  <DollarSign size={24} />
                </div>
                <div className="crm-report-content">
                  <h3>Financial Reports</h3>
                  <p>Revenue analytics, ROI, and cost analysis</p>
                  <div className="crm-report-status">Ready to Develop</div>
                </div>
                <div className="crm-report-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>

              {/* Customer Retention */}
              <div className="crm-report-card clickable">
                <div className="crm-report-icon">
                  <Repeat size={24} />
                </div>
                <div className="crm-report-content">
                  <h3>Customer Retention</h3>
                  <p>Churn analysis and customer lifetime value</p>
                  <div className="crm-report-status">Ready to Develop</div>
                </div>
                <div className="crm-report-arrow">
                  <ChevronRight size={16} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  )
}

export default CRM




